import React, { useContext, useEffect, useMemo, useState } from 'react'
import DataTable, { TableColumn } from 'react-data-table-component';
import { Configure, HitsPerPage, InstantSearch, InstantSearchProps, Pagination, SearchBox, useHits, useInstantSearch, useSortBy } from 'react-instantsearch';
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter';
import { collection, doc, DocumentReference, onSnapshot, query, where } from 'firebase/firestore';
import { FundAutoComplete, FundID } from '../../types';
import { useFirestore } from '../../helpers/firebaseContext';
import CreateFundCampaignButton from './create_campaign_button';
import { ActionIcon, CopyButton, Space, Tooltip } from '@mantine/core';
import { Button } from '@mantine/core';
import { Title } from '@mantine/core';
import { Group } from '@mantine/core';
import { BsPlusCircle } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import { IconCheck, IconCopy, IconRefresh } from '@tabler/icons-react';
import { useSessionStorage } from '@mantine/hooks';
import { MyUserDataContext } from '../../contexts';

const TABLE_KEY = "fund_reports_factsheets";
const TYPESENSE_COLLECTION = import.meta.env.VITE_TYPESENSE_FUND_DOCS_COLLECTION;

interface InitialState {
    query?: string;
    page?: number;
    sortBy?: string;
    hitsPerPage?: number;
}

interface TypesenseFundReport {
    id?: string;
    objectID?: string;
    TITLE?: string;
    FUND_NAME_EN?: string;
    FUND_ID?: FundID | string;
    DATE?: unknown;
    PDF?: string;
    CAMPAIGN_ID?: string;
    path?: string;
    refPath?: string;
    docPath?: string;
    documentPath?: string;
    firestorePath?: string;
}

const toReportDate = (value: unknown): Date | null => {
    if (!value) return null;

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === "number") {
        const parsedDate = new Date(value > 1_000_000_000_000 ? value : value * 1000);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    if (typeof value === "string") {
        const parsedDate = new Date(value);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    if (typeof value === "object" && value !== null) {
        if ("toDate" in value && typeof value.toDate === "function") {
            const parsedDate = value.toDate();
            return parsedDate instanceof Date && !Number.isNaN(parsedDate.getTime()) ? parsedDate : null;
        }

        if ("seconds" in value && typeof value.seconds === "number") {
            const parsedDate = new Date(value.seconds * 1000);
            return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
        }
    }

    return null;
};

const getMediaID = (pdf?: string) => pdf?.split("MediaArchive:").pop() ?? "";

const getDocumentPath = (row: TypesenseFundReport) => {
    const path = row.firestorePath ?? row.documentPath ?? row.docPath ?? row.refPath ?? row.path;

    if (path) {
        return path.replace(/^\/+/, "");
    }

    const id = row.id ?? row.objectID;
    return id ? `${TABLE_KEY}/${id}` : undefined;
};

const TableDisplay = ({ funds, indexName }: { funds: FundAutoComplete[]; indexName: string }) => {
    const db = useFirestore();
    const { items } = useHits<TypesenseFundReport>();
    const { refine } = useSortBy({
        items: [
            { label: "Date (asc)", value: `${indexName}/sort/DATE:asc` },
            { label: "Date (desc)", value: `${indexName}/sort/DATE:desc` },
        ],
    });

    const columns: TableColumn<TypesenseFundReport>[] = [
        {
            name: "Document Title",
            id: "TITLE",
            selector: (row) => row.TITLE ?? "",
            sortable: true,
        },
        {
            name: "Fund Name",
            id: "FUND_NAME_EN",
            selector: (row) => row.FUND_NAME_EN ?? "",
            sortable: true,
        },
        {
            name: "Date / quarter",
            id: "DATE",
            selector: (row) => toReportDate(row.DATE)?.getTime() ?? 0,
            cell: (row) => toReportDate(row.DATE)?.toLocaleDateString("en-GB") ?? "",
            sortable: true,
        },
        {
            name: "PDF",
            cell: (row) => {
                const mediaID = getMediaID(row.PDF);

                return (
                    <CopyButton value={`https://www.kfhcapital.com.kw/media/${mediaID}`} timeout={2000}>
                        {({ copied, copy }) => (
                            <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="right">
                                <ActionIcon color={copied ? 'teal' : 'gray'} variant="subtle" onClick={copy}>
                                    {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                                </ActionIcon>
                            </Tooltip>
                        )}
                    </CopyButton>
                );
            },
        },
        {
            name: "",
            cell: (row) => {
                const chosenFund = funds?.find((fund) => fund.id === row.FUND_ID);
                const documentPath = getDocumentPath(row);
                const documentRef = documentPath ? doc(db, documentPath) as DocumentReference : undefined;
                const reportDate = toReportDate(row.DATE);

                return (
                    <CreateFundCampaignButton
                        variables={{
                            mediaID: getMediaID(row.PDF),
                            fundName: chosenFund?.label,
                            date: reportDate?.toLocaleDateString("en-GB") ?? "",
                        }}
                        fund={chosenFund}
                        documentRef={documentRef}
                        existingCampaignId={row.CAMPAIGN_ID}
                    />
                );
            },
        },
    ];

    return (
        <DataTable
            columns={columns}
            data={items}
            sortServer
            onSort={(column, sortDirection) => {
                if (column.id) {
                    refine(`${indexName}/sort/${column.id}:${sortDirection}`);
                }
            }}
            pagination={false}
        />
    );
};

const RefreshSearchButton = () => {
    const { refresh } = useInstantSearch();

    return (
        <Button leftSection={<IconRefresh size={16} />} onClick={() => refresh()}>
            Refresh
        </Button>
    );
};

const FundReportsInstantSearch = ({ funds }: { funds: FundAutoComplete[] }) => {
    const userData = useContext(MyUserDataContext);
    const indexName = TYPESENSE_COLLECTION;

    const typesenseInstantsearchAdapter = useMemo(() => new TypesenseInstantSearchAdapter({
        server: {
            apiKey: userData.customSearchKey!,
            nodes: [
                {
                    host: import.meta.env.VITE_TYPESENSE_SEARCH_HOST!,
                    port: 443,
                    protocol: 'https',
                },
            ],
        },
        additionalSearchParameters: {
            query_by: "TITLE,FUND_NAME_EN,FUND_ID",
        },
    }), [userData.customSearchKey]);

    const typesenseSearchClient = typesenseInstantsearchAdapter.searchClient;
    const searchStateScope = `${userData.user?.uid ?? 'anon'}_${(userData.customSearchKey ?? '').slice(0, 12)}`;
    const [initialUIStateForIndex, setInitialUIStateForIndex] = useSessionStorage<InitialState>({
        key: `initialUIStateFor_${indexName}_${searchStateScope}`,
        getInitialValueInEffect: false,
    });

    const initialIndexUiState = useMemo(() => ({
        ...(initialUIStateForIndex ?? {}),
        sortBy: `${indexName}/sort/DATE:desc`,
    }), [indexName, initialUIStateForIndex]);

    const onStateChange: InstantSearchProps['onStateChange'] = ({ uiState, setUiState }) => {
        setInitialUIStateForIndex({
            ...(uiState[indexName] ?? {}),
            sortBy: `${indexName}/sort/DATE:desc`,
        });
        setUiState(uiState);
    };

    return (
        <InstantSearch
            searchClient={typesenseSearchClient}
            indexName={indexName}
            initialUiState={{ [indexName]: initialIndexUiState }}
            onStateChange={onStateChange}
        >
            <Configure exactOnSingleWordQuery="attribute" />
            <Group justify="end" mb="md">
                <RefreshSearchButton />
                <SearchBox
                    autoFocus
                    placeholder="Search reports..."
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        width: "50%",
                    }}
                />
            </Group>
            <TableDisplay funds={funds} indexName={indexName} />
            <Group justify="end" mt="md">
                <HitsPerPage
                    items={[
                        { label: '250', value: 250 },
                        { label: '100', value: 100 },
                        { label: '50', value: 50, default: true },
                    ]}
                />
            </Group>
            <Space h="xl" />
            <Pagination
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                }}
            />
        </InstantSearch>
    );
};

const FundReportsList = () => {

    const db = useFirestore();
    const [funds, setFunds] = useState([] as FundAutoComplete[]);
    const navigate = useNavigate();

    //get funds
    useEffect(() => {

        const fundCollection = collection(db, "funds");
        const q = query(fundCollection,
            where("kfhc_website_id", ">=", ""),
            // where("private", "==", true)
        );

        return onSnapshot(q, (snapshot) => {
            if (snapshot.metadata.fromCache) {
                console.log("Cached data", snapshot.docs.length);

            } else {
                console.log("Server data", snapshot.docs.length);
            }


            let fundArray = [] as FundAutoComplete[];

            snapshot.docs.forEach((doc) => {
                var data = doc.data() as FundAutoComplete;
                //TODO: for now, only return funds with ibans available (open ended).  Later, we need to have a flag for the type of fund
                // if (!data.iban) return;
                data["id"] = doc?.id as FundID;
                data["value"] = doc?.id;
                data["label"] = `${data?.name_en} - ${data?.name_ar} `

                fundArray.push(data);
                // return data;
            });
            // console.log("funds", rows);
            setFunds(fundArray);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    return (
        <div>

            <Group justify="space-between">
                <Title>Fund Factsheets</Title>
                <Button
                    leftSection={<BsPlusCircle />}
                    onClick={() => {
                        navigate("/funds-reports/create");
                    }}
                >
                    {" "}
                    Add Report/Factsheet{" "}
                </Button>
            </Group>

            <Space h="xl" />
            <FundReportsInstantSearch funds={funds} />

        </div>
    )
}

export default FundReportsList
